import { useQuery } from '@tanstack/react-query';
import type { PatientSearchResult } from '@wodi/shared';
import type { SearchPickerStatus } from '@wodi/ui';
import { useEffect, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { searchPatients } from '../api/patients.ts';
import { useOnline } from '../hooks/use-online.ts';

/** Wait for a pause in typing before searching; a scanner's burst becomes one search. */
export const SEARCH_DEBOUNCE_MS = 250;
const MIN_CHARS = 2;

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, ms]);
  return debounced;
}

export interface PatientSearchState {
  status: SearchPickerStatus;
  items: PatientSearchResult[];
  /** The text the items answer. */
  itemsQuery: string;
  errorMessage: string | undefined;
  retry: () => void;
}

/**
 * Local patient search as the person types (POST /patients/search). The term lives in
 * component state and the request body only; it never reaches the URL. Results are kept
 * in memory briefly and are cleared with everything else on sign-out and idle timeout.
 */
export function usePatientSearch(text: string): PatientSearchState {
  const online = useOnline();
  const trimmed = text.trim();
  const debounced = useDebounced(trimmed, SEARCH_DEBOUNCE_MS);
  const search = useQuery({
    queryKey: ['patient-search', debounced],
    queryFn: () => searchPatients(debounced),
    enabled: online && debounced.length >= MIN_CHARS,
    staleTime: 0,
    gcTime: 30_000,
    retry: false,
  });

  let status: SearchPickerStatus;
  if (trimmed.length < MIN_CHARS) status = 'idle';
  else if (!online) status = 'offline';
  else if (debounced !== trimmed || search.isFetching || search.isPending) status = 'searching';
  else if (search.isError) status = 'error';
  else status = 'ready';

  const error = search.error;
  return {
    status,
    items: search.data?.items ?? [],
    itemsQuery: debounced,
    errorMessage:
      error === null
        ? undefined
        : error instanceof ApiError && error.code === 'offline'
          ? 'Search needs a connection to Wodi. Reconnect, then search again.'
          : error instanceof ApiError
            ? error.message
            : 'Search did not work. Try again.',
    retry: () => {
      void search.refetch();
    },
  };
}

/**
 * A scanned or typed identifier or patient number that matched exactly. Only these are
 * picked by Enter alone; a phone or name match always needs a deliberate choice.
 */
export function isExactPatientMatch(result: PatientSearchResult): boolean {
  return result.matchedOn.includes('patient_number') || result.matchedOn.includes('identifier');
}
